import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {useTranslation} from '#imports';
import {readonlyState} from '@malt/utils';
import type {FetchError, FreelancersList} from '@api';
import {useApis, useErrorHandler} from '~/composables';
import type {FreelancerProfile} from '../pool-of-freelances/types';

export const usePoolOfFreelancesStore = defineStore('pool-of-freelances', () => {
    const isLoading = ref(false);
    const favoritesLists = ref<FreelancersList[]>([]);
    const totalListsCount = ref(0);
    const lastFavoriteListId = ref<string | null>(null);
    const searchTerm = ref<string>();

    const hasMoreResults = computed(() => favoritesLists.value.length < totalListsCount.value);

    const {t} = useTranslation();
    const {poolOfFreelancersApi} = useApis();
    const {handlePoolOfFreelancesApiError} = useErrorHandler();

    /**
     * Fetch the first freelancers lists page according to given search terms.
     */
    async function fetchFreelancersLists(newSearchTerm?: string) {
        isLoading.value = true;
        searchTerm.value = newSearchTerm?.trim() === '' ? undefined : newSearchTerm?.trim();
        try {
            const result = await poolOfFreelancersApi.getLists({q: searchTerm.value});
            favoritesLists.value = result.lists;
            totalListsCount.value = result.totalListsCount;
            lastFavoriteListId.value = result.lastFavoriteListId ?? null;
        } catch (e) {
            handlePoolOfFreelancesApiError(e as FetchError);
        }
        isLoading.value = false;
    }

    /**
     * Fetch next freelancers lists page according to current search terms and pagination state.
     */
    async function fetchMoreFreelancersLists() {
        isLoading.value = true;
        try {
            const result = await poolOfFreelancersApi.getLists({
                q: searchTerm.value,
                afterListId: lastFavoriteListId.value ?? undefined,
            });
            favoritesLists.value = result.lists;
            totalListsCount.value = result.totalListsCount;
            lastFavoriteListId.value = result.lastFavoriteListId ?? null;
        } catch (e) {
            handlePoolOfFreelancesApiError(e as FetchError);
        }
        isLoading.value = false;
    }

    /**
     * Returns whether or not the given listId contains the given profileId.
     */
    function isFreelancerInFavoriteList(listId: string, profileId: string) {
        const list = favoritesLists.value?.find((list) => list.id === listId);
        return list?.profiles.includes(profileId) ?? false;
    }

    /**
     * Create a new freelancer list and add the given profile into.
     * @param name the name of the list
     * @param sharedWithinCompany whether or not this list is shared with company members
     * @param freelancerProfileIdToAdd the profileId of the freelancer to put initially in the created list
     */
    async function createNewFreelancersList(name: string, sharedWithinCompany: boolean, freelancerProfileIdToAdd: string) {
        try {
            return await poolOfFreelancersApi.createList({
                createFreelancersListRequest: {
                    name,
                    sharedWithinCompany,
                    initialProfileIdToAdd: freelancerProfileIdToAdd,
                },
            });
        } catch (error) {
            handlePoolOfFreelancesApiError(error as FetchError, t('customer.freelancer.pof.modal.create.list.notif.failed', [name]));
        }
    }

    /**
     * Add the given profile to a list.
     */
    async function addToFavorite(profile: FreelancerProfile, listId: string) {
        try {
            await poolOfFreelancersApi.addProfileToFavoriteList({
                profileId: profile.id,
                listId,
            });
            const foundList = favoritesLists.value?.find((list) => list.id === listId);
            if (foundList) {
                foundList.count++;
                foundList.profiles.push(profile.id);
                foundList.firstProfiles = [
                    {
                        fullName: profile.fullName,
                        photoAbsoluteUrl: profile.photoAbsoluteUrl,
                    },
                    ...foundList.firstProfiles,
                ];
            }
        } catch (e) {
            handlePoolOfFreelancesApiError(e as FetchError);
        }
    }

    /**
     * Remove the given profile from a list.
     */
    async function removeFromFavorite(profile: FreelancerProfile, listId: string) {
        try {
            await poolOfFreelancersApi.removeProfileFromFavoriteList({
                profileId: profile.id,
                listId,
            });

            const foundList = favoritesLists.value?.find((list) => list.id === listId);
            if (foundList) {
                const indexInProfileList = foundList.profiles.indexOf(profile.id);

                if (indexInProfileList !== -1) {
                    foundList.count--;
                    foundList.profiles.splice(indexInProfileList, 1);
                }

                const indexInFirstProfilesList = foundList.firstProfiles.findIndex((firstProfile) => firstProfile.fullName === profile.fullName);
                if (indexInFirstProfilesList !== -1) {
                    foundList.firstProfiles.splice(indexInFirstProfilesList, 1);
                }
            }
        } catch (e) {
            handlePoolOfFreelancesApiError(e as FetchError);
        }
    }

    return {
        isLoading: readonlyState(isLoading),
        favoritesLists: readonlyState(favoritesLists),
        hasMoreResults: readonlyState(hasMoreResults),
        searchTerm: readonlyState(searchTerm),
        fetchFreelancersLists,
        fetchMoreFreelancersLists,
        isFreelancerInFavoriteList,
        createNewFreelancersList,
        addToFavorite,
        removeFromFavorite,
    };
});
